import React, { useRef, useCallback } from "react";
import { Form, Input, DatePicker, Button, Row, Col, Select, Typography } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import ScheduleTag from "../../shared/ScheduleTag";
import { useScheduleTags } from "../../shared/useScheduleTags";
import { buildScheduleTagFilterOptions } from "../../shared/scheduleTagOptions";

const { RangePicker } = DatePicker;

/**
 * 计划草案过滤器组件
 *
 * @param {boolean} autoApply - 自动应用模式：表单值变化自动触发 onSearch（防抖 500ms），隐藏搜索/重置按钮
 */
const ScheduleDraftFilter = ({ onSearch, onReset, autoApply = false }) => {
    const [form] = Form.useForm();
    const debounceRef = useRef(null);
    const { activeDefinitions, definitionMap, isLoading, loadError } = useScheduleTags({ includeInactive: true });

    const handleSearch = useCallback(() => {
        form.validateFields().then((values) => {
            onSearch(values);
        });
    }, [form, onSearch]);

    // 重置表单
    const handleReset = useCallback(() => {
        form.resetFields();
        onReset();
    }, [form, onReset]);

    // autoApply: 表单值变化自动触发搜索（防抖）
    const handleValuesChange = useCallback(() => {
        if (!autoApply) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            handleSearch();
        }, 500);
    }, [autoApply, handleSearch]);

    const tagOptions = buildScheduleTagFilterOptions(activeDefinitions);

    return (
        <Form form={form} layout="horizontal" onFinish={(values) => onSearch(values)} className="filter-form" labelAlign="right" onValuesChange={handleValuesChange}>
            <Row gutter={24}>
                <Col span={6}>
                    <Form.Item label="草案名称" name="name">
                        <Input placeholder="请输入草案名称" allowClear />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="飞机" name="aircraft_id">
                        <AircraftSelector
                            valueType="id"
                            mode="multiple"
                            placeholder="请选择飞机"
                            maxTagCount="responsive"
                            displayMode="simple"
                            allowClear
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="标签" name="tags">
                        <Select
                            mode="multiple"
                            placeholder="请选择标签"
                            allowClear
                            loading={isLoading}
                            options={tagOptions}
                            optionRender={(option) => (
                                <ScheduleTag name={option.value} definitionMap={definitionMap} showInactive={false} />
                            )}
                            tagRender={({ value, closable, onClose }) => (
                                <ScheduleTag
                                    name={value}
                                    definitionMap={definitionMap}
                                    closable={closable}
                                    onClose={onClose}
                                    showInactive={false}
                                    style={{ marginBottom: 0 }}
                                />
                            )}
                        />
                        {loadError ? <Typography.Text type="danger">标签配置未加载</Typography.Text> : null}
                    </Form.Item>
                </Col>
                {!autoApply && (
                    <Col span={6} style={{ textAlign: "right" }}>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" icon={<SearchOutlined />} style={{ marginRight: 8 }}>
                                搜索
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                重置
                            </Button>
                        </Form.Item>
                    </Col>
                )}
            </Row>
        </Form>
    );
};

export default ScheduleDraftFilter;
